import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, DollarSign, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeeBands = () => {
    const [feeBands, setFeeBands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBand, setEditingBand] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        first_student_fee: 0,
        second_student_fee: 0,
        additional_student_fee: 0
    });

    useEffect(() => {
        fetchFeeBands();
    }, []);

    const fetchFeeBands = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/fees/bands`, { withCredentials: true });
            setFeeBands(response.data);
        } catch (error) {
            toast.error('Failed to load fee bands');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingBand) {
                await axios.put(`${API_URL}/api/fees/bands/${editingBand.id}`, formData, { withCredentials: true });
                toast.success('Fee band updated');
            } else {
                await axios.post(`${API_URL}/api/fees/bands`, formData, { withCredentials: true });
                toast.success('Fee band created');
            }
            setDialogOpen(false);
            resetForm();
            fetchFeeBands();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save fee band');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (band) => {
        setEditingBand(band);
        setFormData({
            name: band.name,
            first_student_fee: band.first_student_fee,
            second_student_fee: band.second_student_fee || 0,
            additional_student_fee: band.additional_student_fee || 0
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this fee band?')) return;
        try {
            await axios.delete(`${API_URL}/api/fees/bands/${id}`, { withCredentials: true });
            toast.success('Fee band deleted');
            fetchFeeBands();
        } catch (error) {
            toast.error('Failed to delete fee band');
        }
    };

    const resetForm = () => {
        setEditingBand(null);
        setFormData({ name: '', first_student_fee: 0, second_student_fee: 0, additional_student_fee: 0 });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="fee-bands-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Fee Bands</h1>
                        <p className="text-[#4B5563]">Manage tuition fee structures</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-fee-band-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add Fee Band
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {feeBands.map((band) => (
                        <Card key={band.id} className="card-surface">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="h-12 w-12 rounded-xl bg-[#D97706]/10 flex items-center justify-center">
                                        <DollarSign className="h-6 w-6 text-[#D97706]" />
                                    </div>
                                    <div className="flex space-x-2">
                                        <Button size="sm" variant="ghost" onClick={() => handleEdit(band)}>
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDelete(band.id)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-[#1F2937] mb-2">{band.name}</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-[#4B5563]">1st Student Fee</span>
                                        <span className="font-semibold text-[#1F2937] tabular-nums">{'\u20AC'}{band.first_student_fee?.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#4B5563]">2nd Student Fee</span>
                                        <span className="font-semibold text-[#1F2937] tabular-nums">{'\u20AC'}{(band.second_student_fee || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#4B5563]">3+ Student Fee</span>
                                        <span className="font-semibold text-[#1F2937] tabular-nums">{'\u20AC'}{(band.additional_student_fee || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    {feeBands.length === 0 && (
                        <Card className="card-surface col-span-full">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <DollarSign className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">No fee bands created yet</p>
                                <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]">
                                    <Plus className="h-4 w-4 mr-2" /> Create First Fee Band
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">{editingBand ? 'Edit Fee Band' : 'Add New Fee Band'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Band Name</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Band 1, Weekend Program" required />
                            </div>
                            <div className="space-y-2">
                                <Label>1st Student Fee ({'\u20AC'})</Label>
                                <Input type="number" step="0.01" value={formData.first_student_fee} onChange={(e) => setFormData({...formData, first_student_fee: parseFloat(e.target.value) || 0})} required />
                            </div>
                            <div className="space-y-2">
                                <Label>2nd Student Fee ({'\u20AC'})</Label>
                                <Input type="number" step="0.01" value={formData.second_student_fee} onChange={(e) => setFormData({...formData, second_student_fee: parseFloat(e.target.value) || 0})} required />
                            </div>
                            <div className="space-y-2">
                                <Label>3+ Student Fee ({'\u20AC'})</Label>
                                <Input type="number" step="0.01" value={formData.additional_student_fee} onChange={(e) => setFormData({...formData, additional_student_fee: parseFloat(e.target.value) || 0})} required />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    {editingBand ? 'Update' : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default FeeBands;
